import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, User, DollarSign, Check, X, Plus } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

interface Subscription {
  id: string;
  user_id: string;
  subscription_type: string; // Changed from plan_type
  subscription_status: string; // Changed from status
  activated_at: string; // Changed from start_date
  expires_at: string; // Changed from end_date
  amount_paid: number; // Changed from amount
  currency: string;
  user_profile?: {
    first_name?: string;
    last_name?: string;
    email?: string;
  };
}

interface UserProfile {
  user_id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}

export const SubscriptionManagement = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');
  const [userInfo, setUserInfo] = useState<UserProfile | null>(null);
  const [planType, setPlanType] = useState<string>('6-month');
  const [amount, setAmount] = useState<string>('39.99');
  const [activating, setActivating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { toast } = useToast();

  const fetchSubscriptions = async () => {
    try {
      // First get subscriptions from the correct table
      const { data: subscriptionsData, error: subsError } = await supabase
        .from('user_subscriptions')
        .select('*')
        .order('created_at', { ascending: false });

      if (subsError) throw subsError;

      // Then get user profiles for each subscription
      if (subscriptionsData && subscriptionsData.length > 0) {
        const userIds = subscriptionsData.map(sub => sub.user_id);
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('user_id, first_name, last_name, email')
          .in('user_id', userIds);

        if (profilesError) throw profilesError;

        // Create a map for easy lookup
        const profileMap = new Map();
        profilesData?.forEach(profile => {
          profileMap.set(profile.user_id, profile);
        });

        const formattedData = subscriptionsData.map(sub => ({
          ...sub,
          user_profile: profileMap.get(sub.user_id) || {}
        }));

        setSubscriptions(formattedData);
      } else {
        setSubscriptions([]);
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      toast({
        title: "Error",
        description: "Failed to load subscriptions",
        variant: "destructive",
      });
    }
  };

  const searchUserByEmail = async (email: string) => {
    if (!email) {
      setUserInfo(null);
      setSelectedUser('');
      return;
    }

    try {
      const { data: userData, error } = await supabase
        .from('profiles')
        .select('user_id, first_name, last_name, email')
        .eq('email', email)
        .single();

      if (error) {
        setUserInfo(null);
        setSelectedUser('');
        return;
      }

      setUserInfo(userData);
      setSelectedUser(userData.user_id);
    } catch (error) {
      console.error('Error searching user:', error);
      setUserInfo(null);
      setSelectedUser('');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchSubscriptions();
      setLoading(false);
    };
    
    loadData();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchUserByEmail(userEmail);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [userEmail]);

  useEffect(() => {
    if (planType === '6-month') {
      setAmount('39.99');
    }
  }, [planType]);

  const handleActivateSubscription = async () => {
    if (!selectedUser || !planType) {
      toast({
        title: "Error",
        description: "Please search for a user and select a plan type",
        variant: "destructive",
      });
      return;
    }

    setActivating(true);
    try {
      console.log('Activating subscription for user:', selectedUser, 'Plan:', planType, 'Amount:', amount);
      
      const { data, error } = await supabase.rpc('activate_user_subscription', {
        target_user_id: selectedUser,
        plan_type_param: planType,
        amount_param: parseFloat(amount) || 0
      });

      console.log('RPC Response:', { data, error });

      if (error) {
        console.error('Activation error:', error);
        throw error;
      }

      toast({
        title: "Success",
        description: typeof data === 'object' && data !== null && 'message' in data 
          ? String(data.message) 
          : "Subscription activated successfully",
      });

      // Reset form and refresh data
      setSelectedUser('');
      setUserEmail('');
      setUserInfo(null);
      setPlanType('6-month');
      setAmount('39.99');
      setDialogOpen(false);
      await fetchSubscriptions();
    } catch (error: any) {
      console.error('Full error object:', error);
      console.error('Error message:', error.message);
      console.error('Error details:', error.details);
      console.error('Error hint:', error.hint);
      console.error('Error code:', error.code);
      
      toast({
        title: "Error",
        description: `Failed to activate subscription: ${error.message}${error.details ? ` - ${error.details}` : ''}`,
        variant: "destructive",
      });
    } finally {
      setActivating(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { color: 'bg-green-500', icon: Check },
      expired: { color: 'bg-red-500', icon: X },
      cancelled: { color: 'bg-gray-500', icon: X }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.expired;
    const Icon = config.icon;

    return (
      <Badge className={`${config.color} text-white`}>
        <Icon className="w-3 h-3 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysRemaining = (endDate: string) => {
    const today = new Date();
    const end = new Date(endDate);
    const diffTime = end.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return <div className="p-6">Loading subscriptions...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Subscription Management</h2>
          <p className="text-muted-foreground">
            Manage user subscriptions and activate accounts manually
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Activate Subscription
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Activate User Subscription</DialogTitle>
              <DialogDescription>
                Manually activate a subscription for a user account
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="userEmail">Search User by Email</Label>
                <Input
                  id="userEmail"
                  type="email"
                  placeholder="Enter user email..."
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                />
              </div>

              {userInfo && (
                <div className="grid gap-2">
                  <Label>User Information</Label>
                  <div className="p-3 bg-muted rounded-md">
                    <p className="font-medium">{userInfo.first_name} {userInfo.last_name}</p>
                    <p className="text-sm text-muted-foreground">{userInfo.email}</p>
                  </div>
                </div>
              )}
              
              <div className="grid gap-2">
                <Label htmlFor="plan">Plan Type</Label>
                <Select value={planType} onValueChange={setPlanType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6-month">6 Months - €39.99</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="amount">Amount (EUR)</Label>
                <Input
                  id="amount"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="39.99"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
                disabled={activating}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleActivateSubscription}
                disabled={activating || !selectedUser}
              >
                {activating ? "Activating..." : "Activate Subscription"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4">
        {subscriptions.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center">
              <p className="text-muted-foreground">No subscriptions found</p>
            </CardContent>
          </Card>
        ) : (
          subscriptions.map((subscription) => (
            <Card key={subscription.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <User className="w-5 h-5" />
                      {subscription.user_profile?.first_name} {subscription.user_profile?.last_name}
                    </CardTitle>
                    <CardDescription>
                      {subscription.user_profile?.email}
                    </CardDescription>
                  </div>
                  {getStatusBadge(subscription.subscription_status)}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{subscription.subscription_type}</p>
                      <p className="text-muted-foreground">
                        €{subscription.amount_paid} {subscription.currency.toUpperCase()}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Start Date</p>
                      <p className="text-muted-foreground">
                        {formatDate(subscription.activated_at)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium">End Date</p>
                      <p className="text-muted-foreground">
                        {formatDate(subscription.expires_at)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Days Remaining</p>
                      <p className={`text-sm ${
                        getDaysRemaining(subscription.expires_at) < 30 
                          ? 'text-red-500' 
                          : 'text-muted-foreground'
                      }`}>
                        {getDaysRemaining(subscription.expires_at)} days
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};